/**
 * Custom DonutChartComponent - Override Component
 * Auto generated overrides from shared library
 */
import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import * as echarts from 'echarts';
import { DonutChartComponent as ParentDonutChartComponent } from '@shared-lib/chart-components/donut-chart/donut-chart.component';
import { GlobalDropdownComponent } from '../global-dropdown/global-dropdown.component';
import { Pipe, PipeTransform } from '@angular/core';

@Pipe({
  name: 'replaceDot',
  standalone: true
})
export class ReplaceDotPipe implements PipeTransform {
  transform(value: any): string {
    if (!value) return '';
    const stringValue = String(value);
    return stringValue.replace(/\./g, '');
  }
}

@Component({
  selector: 'app-donut-chart',
  standalone: true,
  imports: [CommonModule, GlobalDropdownComponent],
  templateUrl: './donut-chart.component.html',
  styleUrls: ['./donut-chart.component.scss']
})
export class DonutChartComponent extends ParentDonutChartComponent {
  dropdownOpen = false; // Start closed by default
  donutConfig: any = null;
  infoBoxPositions: Map<string, { top: string; left: string; right: string; bottom: string }> = new Map();

  constructor(private http: HttpClient) {
    super();
    this.loadDonutConfig();
  }

  onDropdownToggle(state: boolean) {
    this.dropdownOpen = state;
  }

  /**
   * Calculate automatic positions for info boxes based on donut slices
   */
  private calculateInfoBoxPositions(): void {
    if (!this.chartData || !this.data?.mapping) return;

    // Even angular spacing around the donut for a uniform circular layout.
    const count = this.data.mapping.length || 1;
    const stepDeg = 360 / count;
    const startDeg = Number(this.data?.chartConfig?.infoBoxStartAngle ?? -30);

    const chartDom = document.getElementById(this.data?.widgetId || '');
    const donutWrapper = chartDom?.closest('.donut-wrapper') as HTMLElement | null;
    const wrapperWidth = donutWrapper?.clientWidth || 1000;
    const wrapperHeight = donutWrapper?.clientHeight || 700;
    const chartWidth = chartDom?.clientWidth || wrapperWidth;
    const chartHeight = chartDom?.clientHeight || wrapperHeight;

    // Card dimensions from SCSS for uniform spacing calculations.
    const boxHalfW = 176 / 2;
    const boxHalfH = 112 / 2;

    // Approximate donut outer radius; tuned so cards keep a clean gap from ring.
    const donutOuterRadius = Math.min(chartWidth, chartHeight) * 0.35;
    const donutGapPx = Number(this.data?.chartConfig?.infoBoxGapPx ?? 18);
    const extraOrbitPx = Number(this.data?.chartConfig?.infoBoxExtraOrbitPx ?? 0);

    // Compute one shared radius so all box centers lie on the same circle.
    let requiredRadiusPx = 0;

    for (let index = 0; index < count; index++) {
      const angleDeg = startDeg + index * stepDeg;
      const radians = (angleDeg * Math.PI) / 180;
      const cosAbs = Math.abs(Math.cos(radians));
      const sinAbs = Math.abs(Math.sin(radians));

      const edgeReachX = cosAbs > 0.0001 ? boxHalfW / cosAbs : Number.POSITIVE_INFINITY;
      const edgeReachY = sinAbs > 0.0001 ? boxHalfH / sinAbs : Number.POSITIVE_INFINITY;
      const edgeReachTowardDonut = Math.min(edgeReachX, edgeReachY);
      requiredRadiusPx = Math.max(
        requiredRadiusPx,
        donutOuterRadius + donutGapPx + edgeReachTowardDonut + extraOrbitPx
      );
    }

    // Optional orbit setting can push cards further, but never closer than no-touch radius.
    const orbitRatio = Number(this.data?.chartConfig?.infoBoxOrbit ?? 0.50);
    const preferredRadiusPx = Math.min(wrapperWidth, wrapperHeight) * Math.max(0.35, Math.min(0.75, orbitRatio));
    const sharedRadiusPx = Math.max(requiredRadiusPx, preferredRadiusPx);

    // Custom angle mapping to align boxes with specific slices
    // Mapping: 0:Rev, 1:EBITDA, 2:Liq, 3:Outlook, 4:Vol, 5:Life
    // Desired Angles: Rev:270, EBITDA:-30, Liq:30, Outlook:90, Vol:150, Life:210
    const angleIndices = [5, 0, 1, 2, 3, 4];

    this.data.mapping.forEach((mapping: any, index: number) => {
      const angleIdx = angleIndices[index] ?? index;
      const angleDeg = startDeg + angleIdx * stepDeg;
      const radians = (angleDeg * Math.PI) / 180;

      const offsetXPercent = ((Math.cos(radians) * sharedRadiusPx) / wrapperWidth) * 100;
      const offsetYPercent = ((Math.sin(radians) * sharedRadiusPx) / wrapperHeight) * 100;

      const posXPercent = 50 + offsetXPercent;
      const posYPercent = 50 + offsetYPercent;

      this.infoBoxPositions.set(mapping.label, {
        top: `${posYPercent}%`,
        left: `${posXPercent}%`,
        right: 'auto',
        bottom: 'auto',
      });
    });
  }
  /**
   * Get style object for info box based on label
   */
  getInfoBoxStyle(label: string): any {
    const position = this.infoBoxPositions.get(label);
    if (!position) {
      console.warn('[DonutChart] No position found for label:', label);
      return {};
    }

    // Fine-tune cards to keep them comfortably within viewport.
    const yOffsetByLabel: Record<string, number> = {
      'Remaining Mine Life': -30, // move up even more
      'Revenue': 85, // push down away from top slice
      'EBITDA': 140, // move significantly lower
      'Commodity Volatility': -70, // move further up
      'Commodity Outlook': -140, // move further up into the empty space
      'Mine Outlook': -40, // move slightly down
      'Liquidity runway': -10, // slightly lift from bottom
    };
    
    const xOffsetByLabel: Record<string, number> = {
      'Revenue': 180, // move significantly to the right to avoid overlap
      'EBITDA': 10, // slightly back to left
      'Remaining Mine Life': 25, // move slightly to the right
      'Mine Outlook': -88, // shift left to accommodate wider box without growing right
      'Liquidity runway': 5, // move very very slightly to the right
      'Commodity Volatility': -10, // move very slightly to the left
      'Commodity Outlook': -10, // keep aligned with the commodity slice
    };

    const yOffset = yOffsetByLabel[label] ?? 0;
    const xOffset = xOffsetByLabel[label] ?? 0;

    const topValue = yOffset === 0 ? position.top : `calc(${position.top} + ${yOffset}px)`;
    const leftValue = xOffset === 0 ? position.left : `calc(${position.left} + ${xOffset}px)`;

    return {
      top: topValue,
      left: leftValue,
      transform: label === 'Mine Outlook' ? 'translate(-50%, 0)' : 'translate(-50%, -50%)',
    };
  }

  /**
   * Load donut chart configuration from JSON file
   */
  private loadDonutConfig(): void {
    this.http.get('/assets/donut-chart.json').subscribe({
      next: (config: any) => {
        this.donutConfig = config;
        console.log('[DonutChart] Config loaded:', this.donutConfig);
      },
      error: (err) => {
        console.error('[DonutChart] Failed to load config:', err);
        // Fallback to defaults if JSON fails
      }
    });
  }

  /**
   * Extract metric values from donut config
   * Returns array of metric objects with label, value, color
   */
  getMetricsFromConfig(): any[] {
    if (!this.donutConfig?.metrics) {
      return [];
    }
    return this.donutConfig.metrics.map((m: any) => ({
      label: m.label,
      color: m.color,
      status_key: m.status_key,
      score_key: m.score_key,
      box_class: m.box_class
    }));
  }

  /**
   * Get info box configuration from JSON
   */
  getInfoBoxesConfig(): any[] {
    return this.donutConfig?.infoBoxes || [];
  }

  /**
   * Transform assessment array into flat format expected by parent component
   * Input: [{ assessment: "revenue", score: 1, status: "increasing" }, ...]
   * Output: { revenue_score: 1, revenue_status: "increasing", ... }
   */
  private transformAssessmentToFlat(assessmentArr: any[]): Record<string, any> {
    const mappedFlat: Record<string, any> = {};

    for (const item of assessmentArr) {
      if (!item.assessment) continue;

      const key = item.assessment;

      // Score & status
      mappedFlat[`${key}_score`] = item.score ?? 0;
      const resolvedStatus =
        item.status ??
        item.CommodityStatus ??
        item.commodity_status ??
        item.Status ??
        '';
      mappedFlat[`${key}_status`] = resolvedStatus;

      // 🔥 Dynamically map ALL remaining properties
      Object.keys(item).forEach(prop => {
        if (prop !== 'assessment' && prop !== 'score' && prop !== 'status') {
          mappedFlat[`${key}_${prop}`] = item[prop];
        }
      });
    }

    // Total score
    const validScores = assessmentArr.filter(a => a.score !== null && a.score !== undefined);
    mappedFlat['minesite_assessment_score'] =
      validScores.reduce((sum, a) => sum + a.score, 0);

    console.log('[DonutChart] Dynamic mapped data:', mappedFlat);

    return mappedFlat;
  }

  /**
   * Override ngAfterViewInit to intercept and transform data
   */
  override ngAfterViewInit(): void {
    // Wrap the configService.getConfig method BEFORE calling parent's ngAfterViewInit
    const originalConfigService = (this as any).configService;
    const self = this;
    const originalGetConfig = originalConfigService.getConfig.bind(originalConfigService);

    (this as any).configService.getConfig = function (...args: any[]) {
      return originalGetConfig(...args).pipe(
        map((res: any) => {
          let raw = res?.responseList?.[0]?.['ui-data']?.[0]?.data;

          if (raw) {
            // Transform assessment array into flat format for dropdown/statusData
            if (raw.assessment && Array.isArray(raw.assessment)) {
              raw = self.transformAssessmentToFlat(raw.assessment);
            }

            if (res?.responseList?.[0]?.['ui-data']?.[0]) {
              res.responseList[0]['ui-data'][0].data = raw;
              (self as any).statusData = raw;
            }
          }

          return res;
        })
      );
    };

    // Store the parent instance's buildChart method
    const parentBuildChart = (this as any).buildChart.bind(this);

    // Override buildChart to use mapping.value instead of raw[m.key]
    (this as any).buildChart = () => {
      const chartData: any[] = [];
      const statusData = (this as any).statusData || {};

      if (this.data?.mapping) {
        for (const m of this.data.mapping) {
          const key = `${m.key}_score`;

          chartData.push({
            label: m.label,
            value: m.value ?? 0,
            color: m.color
          });

          // ✅ ADD THIS BLOCK
          const statusKey = `${m.key}_status`;
          const statusValue = statusData[statusKey];

          m.statusText = statusValue ? `Status: ${statusValue}` : null;
        }
      }

      this.chartData = chartData;

      parentBuildChart();
      const chartInstance = echarts.getInstanceByDom(
        document.getElementById(this.data.widgetId) as HTMLElement
      );

      if (chartInstance) {
        const option: any = chartInstance.getOption(); // 👈 CAST HERE

        if (option.series && option.series.length) {
          option.series.forEach((s: any) => {
            // ✅ FIX: Ensure labels use correct field
            s.radius = [
              this.data?.chartConfig?.innerRadius || this.data?.innerRadius || '22%',
              this.data?.chartConfig?.outerRadius || this.data?.outerRadius || '40%'
            ];
            s.center = this.data?.chartConfig?.center || this.data?.center || ['50%', '50%'];
            s.label = {
              show: true,
              position: 'outside',
              formatter: (params: any) => {
                return params.name;
              },
              fontSize: 9,
              fontWeight: 500,
              color: '#333',
              align: 'center',
              overflow: 'break',
              width: 74,
              lineHeight: 12,
              rich: {
                a: {
                  fontSize: 9,
                  fontWeight: 500,
                  color: '#333'
                }
              }
            };
            
            // Add labelLine to connect donut to outside labels
            s.labelLine = {
              show: true,
              length: 8,
              length2: 8,
              lineStyle: {
                color: '#999',
                width: 0.5
              }
            };
            
            // ✅ DISABLE hover zoom/animation
            s.emphasis = {
              disabled: true
            };
          });
        }

        const showTooltip = this.data?.chartConfig?.showTooltip;

        if (showTooltip === false) {
          option.tooltip = {
            show: false,
            trigger: 'none'
          };

          if (option.series && option.series.length) {
            option.series.forEach((s: any) => {
              s.tooltip = { show: false };
            });
          }
        }

        chartInstance.setOption(option);
      }
      self.calculateInfoBoxPositions();
    };

    // Now call parent's ngAfterViewInit
    super.ngAfterViewInit();

    // Log the final statusData that will be used in template
    setTimeout(() => {
      console.log('[DonutChart] Final statusData for template:', (this as any).statusData);
    }, 100);
  }

  /**
   * Format value with unit suffixes only (no year labels - those go on keys).
   */
  formatInfoBoxValue(label: string, value: any): string {
    if (!value) return '';
    const stringValue = String(value);
    let formattedValue = stringValue;

    // Add unit suffixes only
    if (label.includes('Runway') && !/month/i.test(stringValue)) {
      formattedValue = `${stringValue} Months`;
    }
    if ((label.includes('Revenue') || label.includes('EBITDA') || label.includes('Cash Flow') || label.includes('Operating Expense')) && !stringValue.endsWith('M') && !stringValue.endsWith('K')) {
      formattedValue = `${stringValue} M`;
    }

    return formattedValue;
  }

  /**
   * Check if a value contains newlines
   */
  isMultiLine(value: any): boolean {
    if (!value) return false;
    return String(value).includes('\n');
  }

  /**
   * Split multi-line string into cleaned lines for bullet points
   */
  splitLines(value: any): string[] {
    if (!value) return [];
    return String(value)
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .map(line => line.replace(/^[\-\•\*]\s*/, '')); // Remove leading dashes/dots if present in raw data
  }
}


